import {
	ChangeEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import {
	SHIPMENT_EDIT_WINDOW, TModifierForm, TRaisedClaim, TShipmentOrder, TStopModifierForm
} from '@types';
import { usePostModifierAdjustment } from '@api';
import { isEmpty } from '@utils';
import { debounce } from 'lodash';

const PALLET_JACK_HANDLING_KEY = 'pallet_jack_required';

export const defaultModifierForm: TModifierForm = {
	pallet_jack: false,
	stops: [],
	use_user_input_cost: false,
	user_manually_cost: null,
	user_manually_fuel_levy: null,
	use_global_fuel_levy: false,
};

/* A quote is worked out but never applied, so it accepts neither figure. */
const QUOTE_ACCEPTANCE = { accept_shipper_price: false, accept_carrier_cost: false };

type TModifierValues = Pick<TModifierForm, 'pallet_jack' | 'stops'>;

type TSubmitOptions = {
	onSuccess?: (raisedClaims: TRaisedClaim[]) => void;
	onError?: (errPayload: { errors?: Record<string, string[]>; message: string }) => void;
}

type TModifierFormOptions = {
	onFormChange?: () => void;
	onRecalcSuccess?: () => void;
	onRecalcError?: (errPayload: { errors?: Record<string, string[]>; message: string }) => void;
}

/* The shipment's current modifier state, in the shape the endpoint expects.
   Pallet jack is stored on the shipment, tailgate and hand unload per stop. */
const buildModifierValues = (shipment: TShipmentOrder | null): TModifierValues => {
	const stops: TStopModifierForm[] = [];
	shipment?.cargos?.forEach((cargo) => {
		([cargo.pickup, cargo.dropoff]).forEach((stop) => {
			if (!stop || stops.some((s) => s.address_id === stop.id)) return;
			stops.push({
				address_id: stop.id,
				tailgate: !!stop.requires_tailgate_lift,
				hand_unload: !!stop.requires_hand_unload,
			});
		});
	});
	return {
		pallet_jack: (shipment?.pallet_handling?.includes(PALLET_JACK_HANDLING_KEY) || shipment?.need_pallet_jack) ?? false,
		stops,
	};
};

export const useModifierForm = (shipment: TShipmentOrder | null, options: TModifierFormOptions = {}) => {
	const { onFormChange, onRecalcSuccess, onRecalcError } = options;

	const onRecalcSuccessRef = useRef(onRecalcSuccess);
	onRecalcSuccessRef.current = onRecalcSuccess;

	const onRecalcErrorRef = useRef(onRecalcError);
	onRecalcErrorRef.current = onRecalcError;

	const [form, setForm] = useState<TModifierForm>(defaultModifierForm);
	const [recalculated, setRecalculated] = useState(false);
	const [newShipperPrice, setNewShipperPrice] = useState(0);
	const [newCarrierCost, setNewCarrierCost] = useState(0);
	const [newCarrierManualCost, setNewCarrierManualCost] = useState(0);
	const [oldShipperPrice, setOldShipperPrice] = useState(0);
	const [oldCarrierCost, setOldCarrierCost] = useState(0);
	const [editWindow, setEditWindow] = useState<SHIPMENT_EDIT_WINDOW>();

	const { loadingModifierAdjustment, callModifierAdjustment } = usePostModifierAdjustment();

	const isAllocated = shipment?.status?.short !== 'Pending';

	const initialModifiers = useMemo(() => buildModifierValues(shipment), [shipment]);
	/* Only re-seed when the shipment's own modifier values change, not on every
	   unrelated edit to the shipment atom. */
	const initialSignature = JSON.stringify(initialModifiers);

	const isDirty = useMemo(() => {
		if (form.pallet_jack !== initialModifiers.pallet_jack) return true;
		return form.stops.some((stop) => {
			const initial = initialModifiers.stops.find((s) => s.address_id === stop.address_id);
			return !initial || initial.tailgate !== stop.tailgate || initial.hand_unload !== stop.hand_unload;
		});
	}, [form, initialModifiers]);

	/* The edit applies to whichever side has a figure to apply: there is nothing for an admin to tick.
	   Past the shipper's invoice the price on record cannot move, but the difference has somewhere else to
	   go - the endpoint raises it as an I&C - so the price is still applied rather than withheld. */
	const acceptShipperPrice = newShipperPrice > 0;
	const acceptCarrierCost = isAllocated && newCarrierCost > 0;

	/* A cost stated by hand, for an allocation with no rate card to be repriced from. */
	const hasStatedCost = form.use_user_input_cost && !isEmpty(form.user_manually_cost, { stringToNum: true });

	/* A modifier change must settle one of the repriced figures; a stated cost alone cannot carry one.
	   With no modifier change, a stated cost is an edit in its own right. */
	const canSubmit = useMemo(
		() => isDirty ? (acceptShipperPrice || acceptCarrierCost) : hasStatedCost,
		[isDirty, acceptShipperPrice, acceptCarrierCost, hasStatedCost],
	);

	const getStop = (addressId: number) => form.stops.find((stop) => stop.address_id === addressId);

	const setPalletJack = (checked: boolean) => {
		setForm((prev) => ({ ...prev, pallet_jack: checked }));
		onFormChange?.();
	};

	const setStopModifier = (addressId: number, key: 'tailgate' | 'hand_unload', checked: boolean) => {
		setForm((prev) => ({
			...prev,
			stops: prev.stops.map((stop) => stop.address_id === addressId ? { ...stop, [key]: checked } : stop),
		}));
		onFormChange?.();
	};

	const updateForm = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value, checked } = e.target;
		const checkFields = ['use_user_input_cost', 'use_global_fuel_levy'];
		/* The two stated cost fields are the only ones typed into here, and they say nothing about the
		   shipper's price - so unlike a modifier change they leave a repriced figure standing. */
		setForm((prev) => ({ ...prev, [name]: checkFields.includes(name) ? checked : value }));
		onFormChange?.();
	};

	const reCalc = useCallback(debounce((shipmentId: number | undefined, payload: TModifierForm) => {
		callModifierAdjustment({
			routes: [`${shipmentId}`],
			payload: {
				...payload, ...QUOTE_ACCEPTANCE, is_confirmed: false
			},
		})
			.then((res) => {
				if (res) {
					const { data } = res.data;
					setEditWindow(data?.edit_window);
					setNewShipperPrice(data?.shipper?.new?.price?.total_price ?? 0);
					if (payload.use_user_input_cost) {
						setNewCarrierManualCost(data?.carrier?.new?.price?.total_price ?? 0);
					} else {
						setNewCarrierCost(data?.carrier?.new?.price?.total_price ?? 0);
					}
					/* What the shipper is charged now, not what their invoice says: past the invoice the price
					   on record stops moving and the differences sit in approved I&Cs, so comparing the new
					   price against the record would report a difference nobody is billed. */
					setOldShipperPrice(
						data?.shipper?.current_total_price
							?? data?.shipper?.original?.price?.total_price
							?? 0,
					);
					setOldCarrierCost(data?.carrier?.original?.price?.total_price ?? 0);
					setRecalculated(true);
					onRecalcSuccessRef.current?.();
				}
			})
			.catch((err) => {
				if (err?.cause) onRecalcErrorRef.current?.(err.cause.response.data);
			});
	}, 1000), []);

	const resetForm = () => {
		setForm({ ...defaultModifierForm, ...buildModifierValues(shipment) });
		setRecalculated(false);
		setNewShipperPrice(0);
		setNewCarrierCost(0);
		setNewCarrierManualCost(0);
		setOldShipperPrice(0);
		setOldCarrierCost(0);
		setEditWindow(undefined);
	};

	const submitModifiers = (opts: TSubmitOptions = {}) => {
		callModifierAdjustment({
			routes: [`${shipment?.id}`],
			payload: {
				...form,
				accept_shipper_price: acceptShipperPrice,
				accept_carrier_cost: acceptCarrierCost,
				is_confirmed: true,
			},
		})
			.then((res) => {
				if (res) {
					/* Read before resetting: past the shipper's invoice the difference goes to an I&C rather
					   than to the price, one per half of the price that moved, and this is the only place
					   the caller is told that happened. */
					const raisedClaims = res.data?.data?.raised_claims ?? [];
					resetForm();
					opts.onSuccess?.(raisedClaims);
				}
			})
			.catch((err) => {
				if (err?.cause) opts.onError?.(err.cause.response.data);
			});
	};

	useEffect(() => {
		resetForm();
	}, [initialSignature]);

	useEffect(() => {
		if (isDirty || form.use_user_input_cost) {
			reCalc(shipment?.id, form);
		} else {
			/* Back to the shipment's own values - drop any pending recalc so a
			   stale price can't land after the change was reverted. */
			reCalc.cancel();
			setRecalculated(false);
			setNewShipperPrice(0);
			setNewCarrierCost(0);
			setNewCarrierManualCost(0);
			setOldShipperPrice(0);
			setOldCarrierCost(0);
			setEditWindow(undefined);
		}
	}, [
		form.pallet_jack,
		form.stops,
		form.use_user_input_cost,
		form.user_manually_cost,
		form.user_manually_fuel_levy,
		form.use_global_fuel_levy,
		isDirty,
	]);

	return {
		form,
		getStop,
		setPalletJack,
		setStopModifier,
		updateForm,
		isAllocated,
		isDirty,
		canSubmit,
		recalculated,
		newShipperPrice,
		newCarrierCost,
		newCarrierManualCost,
		oldShipperPrice,
		oldCarrierCost,
		loadingModifierAdjustment,
		editWindow,
		resetForm,
		submitModifiers,
	};
};
